import { AlertTriangle, Radio, CircleAlert } from "lucide-react";

/**
 * Renders the intraday freshness of a quote / bar.
 * Props:
 *  - freshness: { age_seconds, stale, threshold_seconds } | null
 *  - feed: 'sip' | 'iex'
 *  - session: 'OPEN' | 'PRE_MARKET' | 'AFTER_HOURS' | 'CLOSED'
 */
export default function FreshnessBadge({ freshness, feed, session }) {
  const isSip = (feed || "").toLowerCase() === "sip";
  if (!freshness) {
    return (
      <span
        data-testid="freshness-badge"
        className="mono text-[10px] px-1.5 py-0.5 border border-slate-700 text-slate-500 uppercase tracking-widest"
      >
        no-data
      </span>
    );
  }
  const age = freshness.age_seconds;
  const stale = freshness.stale;

  // Pretty age
  let ageStr = "—";
  if (age != null) {
    if (age < 60) ageStr = `${age}s`;
    else if (age < 3600) ageStr = `${Math.floor(age / 60)}m ${age % 60}s`;
    else ageStr = `${Math.floor(age / 3600)}h ${Math.floor((age % 3600) / 60)}m`;
  }

  let cls = "border-emerald-500/50 bg-emerald-500/10 text-emerald-300";
  let Icon = Radio;
  let label = isSip ? "SIP · Live" : "IEX · Live";
  if (session !== "OPEN") {
    // Market closed / pre / after — show muted
    cls = "border-slate-600 bg-slate-800/40 text-slate-300";
    Icon = Radio;
    label = isSip ? "SIP" : "IEX";
  }
  if (stale) {
    cls = "border-red-500/60 bg-red-500/10 text-red-300";
    Icon = AlertTriangle;
    label = "STALE DATA";
  }

  return (
    <div
      data-testid="freshness-badge"
      data-testid-stale={stale ? "true" : "false"}
      className={`inline-flex items-center gap-1.5 h-6 px-1.5 border mono text-[10px] uppercase tracking-widest ${cls}`}
      title={
        stale
          ? `Last data ${ageStr} old — exceeds ${freshness.threshold_seconds}s threshold while market is open.`
          : `Last data ${ageStr} old` + (isSip ? "" : " · IEX fallback (SIP not active on account)")
      }
    >
      <Icon className="h-2.5 w-2.5" />
      <span className="font-bold">{label}</span>
      <span className="text-slate-500">·</span>
      <span data-testid="freshness-age" className="tabular-nums">{ageStr}</span>
    </div>
  );
}
